<script setup>
import { computed, ref } from 'vue';
import CoursesFilterGroup from '@/js/core/components/parts/courses/CoursesFilterGroup.vue';
import { coursesFilterGroups } from '@/js/core/utils/courses-filter-groups.js';

const props = defineProps({
  views: {
    type: Object,
    required: true,
  },
  viewTypeVisible: {
    type: Boolean,
    default: true,
  },
  additionalClasses: {
    type: Array,
    default: () => [],
  },
  filterGroups: {
    type: Array,
    default: () => coursesFilterGroups,
  },
});

const openGroupId = ref(null);

const selectedFilters = defineModel('filters', {
  type: Object,
  default: () => ({}),
});

const activeView = defineModel('view', {
  type: String,
  required: true,
});

const viewKeys = computed(() => Object.keys(props.views));

const viewLabels = {
  alphabetical: 'All',
  upcoming: 'Upcoming',
};

const formatViewLabel = key => viewLabels[key] ?? key.charAt(0).toUpperCase() + key.slice(1);

const toggleFilterGroup = id => {
  openGroupId.value = openGroupId.value === id ? null : id;
};

const allSelectedLabels = computed(() => {
  const labels = [];
  let hasActiveFilter = false;

  props.filterGroups.forEach(group => {
    const activeIds = selectedFilters.value[group.id];

    if (!activeIds || activeIds.includes('all')) {
      // The alphabet group's allLabel is just "All", so name the group instead.
      labels.push({
        id: 'all',
        label: group.id === 'alphabet' ? `All ${group.title}` : group.allLabel,
        groupId: group.id,
      });
      return;
    }

    hasActiveFilter = true;

    const findLabels = items => {
      items.forEach(item => {
        if (item.sectionTitle && item.choices) {
          findLabels(item.choices);
        } else if (typeof item === 'object') {
          if (activeIds.includes(item.id)) {
            labels.push({ id: item.id, label: item.label, groupId: group.id });
          }
          if (item.children) findLabels(item.children);
        } else if (typeof item === 'string' && activeIds.includes(item)) {
          labels.push({ id: item, label: item, groupId: group.id });
        }
      });
    };
    findLabels(group.options);
  });
  return hasActiveFilter ? labels : [];
});

const removeFilter = (groupId, itemId) => {
  const arr = selectedFilters.value[groupId];
  if (!arr) return;
  const index = arr.indexOf(itemId);
  if (index > -1) {
    arr.splice(index, 1);
    if (arr.length === 0) selectedFilters.value[groupId] = ['all'];
  }
};

const clearAllFilters = () => {
  Object.keys(selectedFilters.value).forEach(key => {
    selectedFilters.value[key] = ['all'];
  });
};
</script>

<template>
  <div :class="['courses-page-filters-section', ...additionalClasses]">
    <div class="courses-page-filters">
      <div v-if="viewTypeVisible" class="courses-page-toggle-view">
        <ul>
          <li
            v-for="key in viewKeys"
            :key="key"
            class="courses-page-toggle-view-tab"
            :class="{ 'is-active': activeView === key }"
          >
            <a href="#" @click.prevent="activeView = key">{{ formatViewLabel(key) }}</a>
          </li>
        </ul>
      </div>
      <div class="courses-page-filter-type">
        <div class="courses-type-faded-container">
          <div class="courses-type-filters">
            <CoursesFilterGroup
              v-for="group in filterGroups"
              :key="group.id"
              v-bind="group"
              v-model="selectedFilters[group.id]"
              :is-open="openGroupId === group.id"
              @toggle="toggleFilterGroup(group.id)"
            />
          </div>
        </div>
        <div v-if="allSelectedLabels.length" class="courses-type-filter__selected-list-container">
          <div class="courses-type-filter__selected-list">
            <div class="courses-type-filter__listing-label">Listing:</div>
            <div
              v-for="item in allSelectedLabels"
              :key="`${item.groupId}:${item.id}`"
              class="courses-type-filter__selected-label"
            >
              {{ item.label }}
              <button
                type="button"
                class="courses-type-filter__remove"
                :aria-label="'Remove ' + item.label + ' filter'"
                @click="removeFilter(item.groupId, item.id)"
              >
                <svg viewBox="0 0 100 100">
                  <line x1="0" y1="0" x2="100" y2="100" stroke="currentColor" stroke-width="10" />
                  <line x1="100" y1="0" x2="0" y2="100" stroke="currentColor" stroke-width="10" />
                </svg>
              </button>
            </div>
          </div>

          <button type="button" class="courses-type-filter__option--clear" @click="clearAllFilters">
            <svg viewBox="0 0 100 100">
              <line x1="0" y1="0" x2="100" y2="100" stroke="currentColor" stroke-width="10" />
              <line x1="100" y1="0" x2="0" y2="100" stroke="currentColor" stroke-width="10" />
            </svg>
            Clear Filters
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
